import { type Ref, ref } from "vue";
import apiGraph from "@/helpers/api/apiGraph";
import graphHelper from "@/helpers/utilities/GraphHelper";

import { Submission, type Assignment } from "@/models/Education/index";
import LogHandler from "@/helpers/utilities/Handlers/LogHandler";

interface SubmissionWithId extends Submission {
  id: string;
  resourcesFolderUrl?: string;
}

interface BatchResponseEntry {
  id: string;
  status: number;
  body?: { value?: unknown[] };
}

interface SubmissionResourceLike {
  id?: string;
  isDirty?: boolean;
  delete?: boolean;
  requestPayload: unknown;
  [key: string]: unknown;
}

interface OutcomeLike {
  id: string;
  "@odata.type": string;
  feedback?: { text: unknown };
  points?: { points: string | number };
}

export const useSubmissionsComposable = () => {
  const submissionsForAssignmentMap: Ref<Record<string, SubmissionWithId[]>> = ref({});

  const submissionForAssignment = (assignmentId: string, submissionId: string) => {
    const submissions = submissionsForAssignmentMap.value[assignmentId];
    if (submissions) {
      return submissions.find(s => s.id === submissionId);
    }
    return null;
  };

  const _upsertSubmission = (assignmentId: string, submission: SubmissionWithId) => {
    const submissions = submissionsForAssignmentMap.value[assignmentId];
    if (submissions && submissions.length > 0) {
      const index = submissions.findIndex(s => s.id === submission.id);
      if (index > -1) {
        submissions[index] = submission;
      } else {
        submissions.push(submission);
      }
      submissionsForAssignmentMap.value[assignmentId] = [];
      submissionsForAssignmentMap.value[assignmentId] = submissions;
    }
  };

  const loadSubmissions = async (assignmentList: Assignment[]) => {
    const arr: unknown[] = [];
    for (let i = 0; i < assignmentList.length; i++) {
      arr.push(Submission.generateGraphRequestFromAssignment(assignmentList[i]));
    }

    const result: BatchResponseEntry[] = await graphHelper.makeBatchRequest(arr);
    result.map(resp => {
      if (resp.status === 200) {
        const submissions = (resp.body?.value || []).map(s => Submission.prepareForSkolebordet(s) as SubmissionWithId);
        submissionsForAssignmentMap.value[resp.id] = submissions;
      }
    });
  };

  const loadSubmission = async ({
    extSysApiSectionId,
    assignmentId,
    submissionId
  }: {
    extSysApiSectionId: string;
    assignmentId: string;
    submissionId: string;
  }) => {
    try {
      const result = await apiGraph.get(
        `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}?$expand=resources,submittedResources,outcomes`
      );
      const submission = Submission.prepareForSkolebordet(result.data) as SubmissionWithId;
      _upsertSubmission(assignmentId, submission);
    } catch (e) {
      LogHandler.handleError(e);
    }
  };

  const submitSubmission = async ({
    extSysApiSectionId,
    assignmentId,
    submissionId
  }: {
    extSysApiSectionId: string;
    assignmentId: string;
    submissionId: string;
  }) => {
    await apiGraph.post(
      `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/submit`
    );
    await loadSubmission({ extSysApiSectionId, assignmentId, submissionId });
  };

  const undoSubmitSubmission = async ({
    extSysApiSectionId,
    assignmentId,
    submissionId
  }: {
    extSysApiSectionId: string;
    assignmentId: string;
    submissionId: string;
  }) => {
    await apiGraph.post(
      `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/unsubmit`
    );
    await loadSubmission({ extSysApiSectionId, assignmentId, submissionId });
  };

  const updateSubmissionResources = async ({
    extSysApiSectionId,
    assignmentId,
    submissionId,
    resources
  }: {
    extSysApiSectionId: string;
    assignmentId: string;
    submissionId: string;
    resources: SubmissionResourceLike[];
  }) => {
    const promises: Promise<unknown>[] = [];

    if (resources) {
      resources
        .filter(res => res.isDirty)
        .forEach(res => {
          if (res.delete) {
            promises.push(
              apiGraph
                .delete(
                  `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/resources/${res.id}`
                )
                .then(() => {
                  res.isDirty = false;
                })
            );
          } else if (!res.id) {
            promises.push(
              apiGraph
                .post(
                  `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/resources`,
                  res.requestPayload
                )
                .then(() => {
                  res.isDirty = false;
                })
            );
          }
        });
    }

    await Promise.all(promises);
    await loadSubmission({ extSysApiSectionId, assignmentId, submissionId });
  };

  const publishSubmission = async ({
    extSysApiSectionId,
    assignmentId,
    submissionId
  }: {
    extSysApiSectionId: string;
    assignmentId: string;
    submissionId: string;
  }) => {
    await apiGraph.post(
      `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/return`
    );
    await loadSubmission({ extSysApiSectionId, assignmentId, submissionId });
  };

  const createSubmissionResourcesFolder = async ({
    extSysApiSectionId,
    assignmentId,
    submissionId
  }: {
    extSysApiSectionId: string;
    assignmentId: string;
    submissionId: string;
  }): Promise<unknown> => {
    const noOfRetry = 5;
    let levelOfRetry = 0;

    if (!assignmentId || !extSysApiSectionId) {
      throw new Error("Input not valid");
    }

    const existing = submissionForAssignment(assignmentId, submissionId);
    if (existing?.resourcesFolderUrl && existing.resourcesFolderUrl !== "") {
      return existing.resourcesFolderUrl;
    }

    try {
      const result = await apiGraph
        .post(
          `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/setUpResourcesFolder`
        )
        .catch(async (e: { response?: { status?: number; data?: { error?: { innerError?: { code?: string } } } } }) => {
          if (
            e.response &&
            e.response.status === 400 &&
            e.response.data?.error?.innerError &&
            e.response.data.error.innerError.code === "folderAlreadyExists"
          ) {
            await loadSubmission({ extSysApiSectionId, assignmentId, submissionId });
            return;
          }
        });

      if (result && result.status === 200 && result.data && result.data.resourcesFolderUrl) {
        const submissions = submissionsForAssignmentMap.value[assignmentId];
        if (submissions && submissions.length > 0) {
          const index = submissions.findIndex(s => s.id === submissionId);
          if (index > -1) {
            submissions[index].resourcesFolderUrl = result.data.resourcesFolderUrl;
          }
          submissionsForAssignmentMap.value[assignmentId] = [];
          submissionsForAssignmentMap.value[assignmentId] = submissions;
        }
        return;
      }
    } catch {
      if (noOfRetry > levelOfRetry) {
        levelOfRetry = levelOfRetry + 1;
        return setTimeout(() => {
          return createSubmissionResourcesFolder({ extSysApiSectionId, assignmentId, submissionId });
        }, 2000);
      }
      throw new Error();
    }
  };

  const updateOutcomes = async ({
    extSysApiSectionId,
    assignmentId,
    submissionId,
    outcomes
  }: {
    extSysApiSectionId: string;
    assignmentId: string;
    submissionId: string;
    outcomes: OutcomeLike[];
  }) => {
    if (Array.isArray(outcomes)) {
      const promises = outcomes.map(async outcome => {
        if (outcome["@odata.type"] === "#microsoft.graph.educationFeedbackOutcome") {
          await apiGraph.patch(
            `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/outcomes/${outcome.id}`,
            {
              "@odata.type": "#microsoft.graph.educationFeedbackOutcome",
              feedback: {
                text: outcome.feedback?.text
              }
            }
          );
        }

        if (outcome["@odata.type"] === "#microsoft.graph.educationPointsOutcome" && outcome.points) {
          await apiGraph.patch(
            `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/outcomes/${outcome.id}`,
            {
              "@odata.type": "#microsoft.graph.educationPointsOutcome",
              points: {
                "@odata.type": "#microsoft.graph.educationAssignmentPointsGrade",
                points: parseInt(String(outcome.points.points))
              }
            }
          );
        }
      });

      await Promise.all(promises);
    }
  };

  return {
    submissionsForAssignmentMap,
    loadSubmissions,
    loadSubmission,
    _upsertSubmission,
    submitSubmission,
    undoSubmitSubmission,
    updateSubmissionResources,
    publishSubmission,
    createSubmissionResourcesFolder,
    updateOutcomes
  };
};
